import { useEffect } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useVoiceInput, type VoiceErrorCode } from "@/hooks/useVoiceInput";

export interface VoiceInputButtonProps {
  /** Called with transcribed text after a successful recording. */
  onTranscript: (text: string) => void;
  /** Disable the button externally (e.g. while the chat is offline/loading). */
  disabled?: boolean;
  /** BCP 47 language tag forwarded to the model (default: 'de'). */
  language?: string;
}

/** Map a stable voice error code to its localized message key. */
const ERROR_KEY: Record<VoiceErrorCode, string> = {
  micDenied: "inspectionChat.voice.errorMicDenied",
  noProvider: "inspectionChat.voice.errorNoProvider",
  transcriptionFailed: "inspectionChat.voice.errorTranscription",
};

/**
 * VoiceInputButton (TES-1172, WP6)
 *
 * Push-to-talk mic button for the AI chat input bar. Three visual states:
 *   idle         — mic icon
 *   recording    — stop icon, destructive styling + pulse
 *   transcribing — spinner, disabled
 *
 * Hidden when the browser lacks MediaRecorder/getUserMedia. Errors are surfaced
 * via localized sonner toasts. The transcript is inserted into the input (not
 * auto-sent) by the parent via `onTranscript`.
 */
export function VoiceInputButton({
  onTranscript,
  disabled = false,
  language,
}: VoiceInputButtonProps) {
  const { t } = useTranslation();
  const { isRecording, isTranscribing, isSupported, error, startRecording, stopRecording } =
    useVoiceInput({ onTranscript, language });

  // Surface mic/transcription errors via localized toast.
  useEffect(() => {
    if (error) {
      toast.error(t(ERROR_KEY[error]));
    }
  }, [error, t]);

  if (!isSupported) {
    return null;
  }

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      void startRecording();
    }
  };

  const ariaLabel = isTranscribing
    ? t("inspectionChat.voice.transcribing")
    : isRecording
      ? t("inspectionChat.voice.stopRecording")
      : t("inspectionChat.voice.startRecording");

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleClick}
      disabled={disabled || isTranscribing}
      aria-label={ariaLabel}
      aria-pressed={isRecording}
      className={
        isRecording
          ? "shrink-0 bg-destructive/10 text-destructive ring-2 ring-destructive/50 animate-pulse"
          : "shrink-0"
      }
    >
      {isTranscribing ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isRecording ? (
        <Square className="h-4 w-4" />
      ) : (
        <Mic className="h-4 w-4" />
      )}
    </Button>
  );
}
